import React, { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  Grid2,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import error_image from "../../assets/icons/no_image.svg";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import { ArrowIcon } from "../../components/icon-components/ArrowIcon";
import { CloseIcon } from "../../components/icon-components/CloseIcon";
import { useTranslation } from "react-i18next";
import styles from "./ImageThemeSelector.module.scss";
import { ImageTheme } from "../../utility/interfaces/babyWeekImageDataInterface";
import { GREY_DARK } from "../../utility/constants/common-constants";

interface ImageThemeSelectorProps {
  imageThemes: ImageTheme[];
  onThemeUpdate: (theme: ImageTheme) => void;
  handleSelect: (theme: ImageTheme) => void;
  handleBack: () => void;
  selectedThemeId: number;
}

const ImageThemeSelector: React.FC<ImageThemeSelectorProps> = ({
  imageThemes,
  onThemeUpdate,
  handleSelect,
  handleBack,
  selectedThemeId
}) => {
  const handleUpdate = () => {
    const selectedTheme = imageThemes.find(
      (theme) => theme.imageThemeId === selectedThemeId
    );
    if (selectedTheme) {
      onThemeUpdate(selectedTheme);
    }
  };

  const isMobile = useMediaQuery("(max-width: 900px)");
  const { t } = useTranslation();
  return (
    <Box className={styles.container}>
      {isMobile && (
        <IconButton
          onClick={handleBack}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <Grid2
        container
        alignItems="flex-start"
      >
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className={styles.headerBox}>
            {!isMobile && (
              <IconButton
                onClick={handleBack}
                className={styles.arrowButton}
              >
                <ArrowIcon
                  width={24}
                  height={24}
                  fill="#1A1A1A"
                />
              </IconButton>
            )}

            <Box ml= {isMobile ? 0 : 2 }>
              <Typography
                variant= {isMobile ? "h4" : "h2" }
                mb={".75rem"}
              >
                {t("babyWeekImages.title")}
              </Typography>

              <Typography
                variant="body2"
                mb={"1.5rem"}
              >
                {t("babyWeekImages.subtitle")}
              </Typography>
            </Box>
          </Box>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className={styles.rightContainer}>
            <Box className={styles.alignContent}>
              <Grid2
                container
                spacing={2}
                className={styles.cardGrid}
              >
                {imageThemes.map((theme) => (
                  <Grid2 size={{ xs: 4 }} key={theme.imageThemeId}>
                    <Card
                      variant="outlined"
                      className={`${styles.card} ${
                        selectedThemeId === theme.imageThemeId
                          ? styles.selected
                          : ""
                      }`}
                    >
                      <CardActionArea
                        className={styles.cardContent}
                        onClick={() => handleSelect(theme)}
                      >
                        <CardMedia
                          component="img"
                          height="80"
                          image={theme.imageThemeIconUrl}
                          alt={theme.imageThemeDisplayName}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src = error_image;
                            target.style.cssText = `
                            width: 30px;
                            height: 30px;
                            padding: 10px;
                            margin: auto;`;
                          }}
                          className={styles.cardImage}
                        />
                      </CardActionArea>
                    </Card>
                    <Typography
                      align="center"
                      p={1}
                      className={styles.themeLabel}
                      fontWeight={
                        selectedThemeId === theme.imageThemeId
                          ? "bold"
                          : "normal"
                      }
                    >
                      {theme.imageThemeDisplayName}
                    </Typography>
                  </Grid2>
                ))}
              </Grid2>

              <Box
                display="flex"
                justifyContent="center"
              >
                <CustomButton
                  variant="contained"
                  onClick={handleUpdate}
                  text={t("babyWeekImages.apply")}
                />
              </Box>
            </Box>
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default ImageThemeSelector;
